import {inject, Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {queueEndpoints} from '../../../types/api/endpoints/queue.endpoints';
import { HttpClient } from '@angular/common/http';
import {HttpErrorService} from '../../utilities/http-error/http-error.service';
import {
  QueueAssignment, QueueAssignmentList, QueueBatchAssignmentOfGuid, QueueBatchAssignmentOfQueueAssignment,
  QueueDropdowns,
  QueueInfo, QueueItemList,
  QueueListQuery, QueueTag, QueueType, QueueUserList, QueueUserListSimple,
  QueueUserQuery, ResponseMessages, SelectionValueOfGuid, UserTypes
} from '../../../types/api/dot-net-api-types/data-contracts';
import {Observable} from 'rxjs';
import {UtilitiesService} from '../../utilities/utilities-service.service';

export interface IQueueApiService {
  getQueues(): Observable<QueueInfo[]>;
  getQueueDropdowns(): Observable<QueueDropdowns>;
  getQueueContent(query: QueueListQuery): Observable<QueueItemList[]>;
  getQueueUsersById(queueId: string, query: QueueUserQuery): Observable<QueueUserList[]>;
  getQueueUsersByIdSimple(queueId: string): Observable<QueueUserListSimple[]>;
  isUserAssignedToQueueUsingId(queueId: string, userId: string): Observable<boolean>;
  isUserAssignedToQueueUsingType(queueType: QueueType, userId: string): Observable<boolean>;
  // Item(s) assignment/removal to queues
  assignFilingToQueue(filingIds: string[], queueId: string, isCPM: boolean): Observable<ResponseMessages>;
  assignUserToQueueItems(userId: string, itemIds: string[]): Observable<ResponseMessages>;
  removeFromQueue(itemIds: string[]): Observable<ResponseMessages>;
  // Tags
  getQueueItemsByTagId(tagId: string): Observable<string>;
  removeTagFromSystem(tagId: string): Observable<ResponseMessages>;
  addTagToQueueItem(tagId: string, itemId: string): Observable<string>;
  deleteTagFromQueueItem(itemId: string, tagId: string): Observable<ResponseMessages>;
  getQueueTags(queueId: string): Observable<QueueTag[]>;
  addTagContentToQueueItem(content: string, itemId: string): Observable<ResponseMessages>;
  // User
  getUserAssignedQueues(userId: string): Observable<QueueAssignmentList[]>;
  assignUserToQueues(userId: string, assignments: QueueAssignment[]): Observable<ResponseMessages>;
  updateUserQueuePermissions(userId: string, assignments: QueueAssignment[]): Observable<ResponseMessages>;
  unassignUserFromQueues(userId: string, queueIds: string[]): Observable<ResponseMessages>;
  getUserAllowableQueues(userType: UserTypes): Observable<SelectionValueOfGuid[]>;
  batchAssignUserToQueues(assignments: QueueBatchAssignmentOfQueueAssignment[]): Observable<ResponseMessages>;
  batchUnassignUserFromQueues(queueIds: QueueBatchAssignmentOfGuid[]): Observable<ResponseMessages>;
  // Bookmarks
  toggleBookmark(itemIds: string[], isBookmarked: boolean): Observable<ResponseMessages>;
}

/**
 * Base url for the api.
 */
const BASE_URL = environment.ApiServer;

/**
 * Case Endpoints for the api.
 */
const QUEUE_ENDPOINTS = queueEndpoints(BASE_URL);

@Injectable({
  providedIn: 'root'
})
export class QueueApiService implements IQueueApiService {
  /******************************************
   * Dependencies Injection
   ******************************************/
  private readonly errorService = inject(HttpErrorService);
  private readonly http = inject(HttpClient);
  private readonly utilsService = inject(UtilitiesService);

  /******************************************
   * Queues
   ******************************************/
  /**
   * Returns a collection of queues and their basic information.
   */
  getQueues(): Observable<QueueInfo[]> {
    const url = QUEUE_ENDPOINTS.getQueue();
    return <Observable<QueueInfo[]>>this.http.get(url);
  }

  /**
   * Gets the dropdowns for queue-related views and endpoints.
   */
  getQueueDropdowns(): Observable<QueueDropdowns> {
    const url = QUEUE_ENDPOINTS.getQueueDropdowns();
    return <Observable<QueueDropdowns>>this.http.get(url);
  }

  /**
   * Gets the content of a specific queue assigned to the caller by id.
   * @param query
   */
  getQueueContent(query: QueueListQuery): Observable<QueueItemList[]> {
    const url = QUEUE_ENDPOINTS.getQueueContent();
    return <Observable<QueueItemList[]>>this.http.post(url, query);
  }

  /**
   * Get the users assigned to a specific queue, optionally filtered by role and user type.
   * @param queueId
   * @param query
   */
  getQueueUsersById(queueId: string, query: QueueUserQuery): Observable<QueueUserList[]> {
    const url = QUEUE_ENDPOINTS.getQueueUsersById(queueId);
    return <Observable<QueueUserList[]>>this.http.post(url, query);
  }

  /**
   * Gets a simplified list of useres assigned to a specific queue, filtered by active status.
   * @param queueId
   */
  getQueueUsersByIdSimple(queueId: string): Observable<QueueUserListSimple[]> {
    const url = QUEUE_ENDPOINTS.getQueueUsersByIdSimple(queueId);
    return <Observable<QueueUserListSimple[]>>this.http.get(url);
  }

  /**
   * Returns true or false if the user is part of a provided queue or not using the queue's ID
   */
  isUserAssignedToQueueUsingId(queueId: string, userId: string): Observable<boolean> {
    const url = QUEUE_ENDPOINTS.isUserAssignedToQueueUsingId(queueId, userId);
    return <Observable<boolean>>this.http.get(url);
  }

  /**
   * Returns true or false if the user is part of a provided queue or not using the queue's Type
   */
  isUserAssignedToQueueUsingType(queueType: QueueType, userId: string): Observable<boolean> {
    const url = QUEUE_ENDPOINTS.isUserAssignedToQueueUsingType(queueType, userId);
    return <Observable<boolean>>this.http.get(url);
  }

  /******************************************
   * Assignments
   ******************************************/
  assignFilingToQueue(filingIds: string[], queueId: string, isCPM = false): Observable<ResponseMessages> {
    const url = isCPM ? QUEUE_ENDPOINTS.assignFillingFromCPM(queueId) : QUEUE_ENDPOINTS.assignFillingFromQueueModule(queueId);
    return <Observable<ResponseMessages>>this.http.post(url, filingIds);
  }

  /**
   * Assigns the provided user to the specified queue items, if they are allowed to be assigned to that queue.
   * @param userId
   * @param itemIds
   */
  assignUserToQueueItems(userId: string, itemIds: string[]): Observable<ResponseMessages> {
    let url = QUEUE_ENDPOINTS.assignUserToQueueItems();
    if (!!userId && userId !== '') {
      const params = { userId: userId };
      url = this.utilsService.buildUrlWithParams(QUEUE_ENDPOINTS.assignUserToQueueItems(), params);
    }
    return <Observable<ResponseMessages>>this.http.post(url, itemIds);
  }

  /**
   * Removes the provided items from any queue they may exist in.
   * @param itemIds
   */
  removeFromQueue(itemIds: string[]): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.removeItemsFromQueue();
    return <Observable<ResponseMessages>>this.http.delete(url, {body: itemIds});
  }

  /******************************************
   * Tags
   ******************************************/
  /**
   * Retrieves a list of tags created for a specific queue.
   * @param queueId
   */
  getQueueTags(queueId: string): Observable<QueueTag[]> {
    const url = QUEUE_ENDPOINTS.getQueueTags(queueId);
    return <Observable<QueueTag[]>>this.http.get(url);
  }

  /**
   * Gets all work items that have the specific tag set provided.
   * @param tagId
   */
  getQueueItemsByTagId(tagId: string): Observable<string> {
    // ToDo: 8-12-2025 - This is still stubbed out. Will be finished later.
    const url = QUEUE_ENDPOINTS.getItemsByTagId(tagId);
    return <Observable<string>>this.http.get(url);
  }

  /**
   * Deletes a tag from the system, if it is not currently assigned to any queue items.
   * @param tagId
   */
  removeTagFromSystem(tagId: string): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.deleteTag(tagId);
    return <Observable<ResponseMessages>>this.http.delete(url);
  }

  /**
   * Adds a tag to the specified queue item, if it is not already assigned.
   * @param tagId
   * @param itemId
   */
  addTagToQueueItem(tagId: string, itemId: string): Observable<string> {
    const url = QUEUE_ENDPOINTS.addTagToQueueItem(tagId, itemId);
    return <Observable<string>>this.http.put(url, {});
  }

  /**
   * Adds a tag to the specified queue item if a matching tag is not already assigned. Otherwise, generates the tag.
   * @param content
   * @param itemId
   */
  addTagContentToQueueItem(content: string, itemId: string): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.putTagContentToQueueItem(content, itemId);
    return <Observable<ResponseMessages>>this.http.put(url, {});
  }

  /**
   * Removes a tag from the specified queue item, if it is currently assigned.
   * @param tagId
   * @param itemId
   */
  deleteTagFromQueueItem(itemId: string, tagId: string): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.removeTagFromQueueItem(itemId, tagId);
    return <Observable<ResponseMessages>>this.http.delete(url);
  }

  /******************************************
   * User
   ******************************************/
  /**
   * Gets the queues assigned to the provided user.
   * @param userId
   */
  getUserAssignedQueues(userId: string): Observable<QueueAssignmentList[]> {
    const url = QUEUE_ENDPOINTS.getUserAssignedQueues(userId);
    return <Observable<QueueAssignmentList[]>>this.http.get(url);
  }

  /**
   * Assigns a single user to one or more queues.
   * @param userId
   * @param assignments
   */
  assignUserToQueues(userId: string, assignments: QueueAssignment[]): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.assignUserToQueues(userId);
    return <Observable<ResponseMessages>>this.http.post(url, assignments);
  }

  /**
   * Updates the permissions of a user in a queue based on the provided assignments.
   * @param userId
   * @param assignments
   */
  updateUserQueuePermissions(userId: string, assignments: QueueAssignment[]): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.updateUserQueuePermissions(userId);
    return <Observable<ResponseMessages>>this.http.patch(url, assignments);
  }

  /**
   * Unassigns a user from one or more queues, removing their permissions and any associated work items.
   * @param userId
   * @param queueIds
   */
  unassignUserFromQueues(userId: string, queueIds: string[]): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.unassignUserFromQueues(userId);
    return <Observable<ResponseMessages>>this.http.delete(url, {body: queueIds});
  }

  /**
   * Gets the queues the provided user type is allowed to be assigned to.
   * @param userType
   */
  getUserAllowableQueues(userType: UserTypes): Observable<SelectionValueOfGuid[]> {
    const url = QUEUE_ENDPOINTS.getUserAllowableQueues(userType);
    return <Observable<SelectionValueOfGuid[]>>this.http.get(url);
  }

  /**
   * Batch assign users to queues based on the provided assignments.
   * @param assignments
   */
  batchAssignUserToQueues(assignments: QueueBatchAssignmentOfQueueAssignment[]): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.batchAssignUsersToQueues();
    return <Observable<ResponseMessages>>this.http.post(url, assignments);
  }

  /**
   * Batch unassigns multiple users from one or more queues in a batch operation
   * @param assignments
   */
  batchUnassignUserFromQueues(assignments: QueueBatchAssignmentOfGuid[]): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.batchUnassignUsersToQueues();
    return <Observable<ResponseMessages>>this.http.delete(url, {body: assignments});
  }

  /******************************************
   * Bookmarks
   ******************************************/
  /**
   * Toggle a bookmark.
   * @param itemIds
   * @param isBookmarked
   */
  toggleBookmark(itemIds: string[], isBookmarked: boolean): Observable<ResponseMessages> {
    const url = QUEUE_ENDPOINTS.bookmark();
    if (isBookmarked) {
      return <Observable<ResponseMessages>>this.http.delete(url, {body: itemIds});
    }
    return <Observable<ResponseMessages>>this.http.put(url, itemIds);
  }

}
